import { Divider } from "@mantine/core";
import { HiArrowRight } from "react-icons/hi2";
import { HiCheckCircle } from "react-icons/hi2";
import { HiChevronLeft } from "react-icons/hi2";
import CustomButton from "../../ui/CustomButton/CustomButton";
import { PAYOUT_ACCOUNT_TYPES } from "../config/fundsTransferConfig";
import { formatKES } from "../../../utils/transactionutils";

function DetailRow({ label, value }) {
  return (
    <div className="py-2.5 border-b border-gray-100 last:border-b-0">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-sm font-medium text-gray-900 mt-0.5">{value}</p>
    </div>
  );
}

function DestinationRows({ withdrawalType, account }) {
  const details = account?.details ?? {};

  switch (withdrawalType) {
    case PAYOUT_ACCOUNT_TYPES.MOBILE_MONEY:
      return (
        <>
          <DetailRow label="Provider" value={details.provider ?? "—"} />
          <DetailRow label="Phone Number" value={details.msisdn ?? "—"} />
        </>
      );
    case PAYOUT_ACCOUNT_TYPES.BANK:
      return (
        <>
          <DetailRow label="Bank Name" value={details.bankName ?? "—"} />
          <DetailRow label="Account No." value={details.accountNumber ?? "—"} />
          {details.swiftCode && (
            <DetailRow label="SWIFT / BIC" value={details.swiftCode} />
          )}
          {details.branchCode && (
            <DetailRow label="Branch Code" value={details.branchCode} />
          )}
        </>
      );
    case PAYOUT_ACCOUNT_TYPES.WALLET:
      return (
        <DetailRow label="Account No." value={details.accountNumber ?? "—"} />
      );
    default:
      return null;
  }
}

export function Step2ReviewConfirm({
  withdrawalType,
  selectedPayoutAccount,
  amount,
  transactionFee,
  exciseDuty,
  totalAmount,
  newBalance,
  onBack,
  onConfirm,
  isConfirming,
}) {
  const accountName =
    selectedPayoutAccount?.details?.accountName ??
    selectedPayoutAccount?.display ??
    "—";

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          Withdrawing To
        </p>
        <div className="flex items-center gap-2">
          <HiCheckCircle size={16} className="text-green-500 shrink-0" />
          <span className="text-sm font-semibold text-gray-900">
            {accountName}
          </span>
        </div>
      </div>

      <Divider />

      <div>
        <DestinationRows
          withdrawalType={withdrawalType}
          account={selectedPayoutAccount}
        />
        <DetailRow label="Account Name" value={accountName} />
        <DetailRow label="Amount" value={formatKES(amount)} />
        <DetailRow label="Service Charges" value={formatKES(transactionFee)} />
        <DetailRow label="Excise Duty" value={formatKES(exciseDuty)} />
      </div>

      <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold text-gray-900">Total Debited</span>
          <span className="text-base font-bold text-gray-900">
            {formatKES(totalAmount)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 font-medium">
            Balance After
          </span>
          <span className="text-sm font-semibold text-gray-700">
            {formatKES(newBalance)}
          </span>
        </div>
      </div>

      <div className="flex gap-3 pt-1">
        <CustomButton
          variant="outline"
          color="gray"
          radius="md"
          leftSection={<HiChevronLeft size={16} />}
          onClick={onBack}
          disabled={isConfirming}
          className="flex-1"
        >
          Back
        </CustomButton>
        <CustomButton
          color="primary"
          radius="md"
          rightSection={<HiArrowRight size={16} />}
          onClick={onConfirm}
          loading={isConfirming}
          className="flex-1"
        >
          Confirm Withdrawal
        </CustomButton>
      </div>
    </div>
  );
}
